import { useState, useEffect, useCallback, memo } from "react";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { MEMBERSHIP_URL } from "@/lib/subscriptionAccess";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown,
  Calendar,
  CreditCard,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  History
} from "lucide-react";

// Helper function to get subscription status display
const getStatusDisplay = (subscription) => {
  if (subscription.type === "none") {
    return { icon: CheckCircle, color: "text-green-400", label: "Active" };
  }
  if (subscription.isCancelled) {
    return { icon: XCircle, color: "text-amber-400", label: "Cancelled" };
  }
  if (subscription.isActive) {
    return { icon: CheckCircle, color: "text-green-400", label: "Active" };
  }
  return { icon: XCircle, color: "text-red-400", label: "Expired" };
};

// Helper function to get plan background color
const getPlanBgColor = (type) => {
  switch (type) {
    case "annual": return "bg-[#a69373]/20";
    case "quarterly": return "bg-[#7b9e7b]/20";
    case "monthly": return "bg-[#5a7a9a]/20";
    default: return "bg-white/10";
  }
};

function SubscriptionManagementComponent() {
  const { token } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

  const fetchPaymentHistory = useCallback(async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/subscription/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data.transactions || []);
      setHasLoadedHistory(true);
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  useEffect(() => {
    if (!hasLoadedHistory) {
      fetchPaymentHistory();
    }
  }, [fetchPaymentHistory, hasLoadedHistory]);

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription cancelled successfully");
      setShowCancelConfirm(false);
      refreshSubscription();
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API}/subscription/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Subscription reactivated!");
      refreshSubscription();
    } catch (error) {
      console.error("Reactivate error:", error);
      toast.error(error.response?.data?.detail || "Failed to reactivate. Please purchase a new subscription.");
      // If reactivation fails because subscription expired, redirect to membership page
      if (error.response?.status === 400) {
        window.open(MEMBERSHIP_URL, "_blank");
      }
    } finally {
      setReactivating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-ZA", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const getPlanColor = () => {
    switch (subscription.type) {
      case "annual": return "text-[#a69373]";
      case "quarterly": return "text-[#7b9e7b]";
      case "monthly": return "text-[#5a7a9a]";
      default: return "text-white/60";
    }
  };

  // Show nothing if loading
  if (subscription.loading) {
    return (
      <Card className="glass-card overflow-hidden animate-pulse">
        <CardContent className="p-5">
          <div className="h-32 bg-white/5 rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Plan Card */}
      <Card className="glass-card overflow-hidden" data-testid="subscription-management-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getPlanBgColor(subscription.type)}`}>
              <Crown className={`w-5 h-5 ${getPlanColor()}`} />
            </div>
            <div>
              <h3 className="text-white font-medium">Subscription Management</h3>
              <p className="text-white/50 text-sm">Manage your Compass membership</p>
            </div>
          </div>

          {/* Plan Details */}
          <div className="bg-white/5 rounded-xl p-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider">Current Plan</p>
                <p className={`text-lg font-medium capitalize ${getPlanColor()}`}>
                  {subscription.type === "none" ? "Free Plan" : subscription.type}
                </p>
              </div>
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider">Status</p>
                <div className="flex items-center gap-1.5">
                  {(() => {
                    const status = getStatusDisplay(subscription);
                    const StatusIcon = status.icon;
                    return (
                      <>
                        <StatusIcon className={`w-4 h-4 ${status.color}`} />
                        <span className={`${status.color} text-sm`}>{status.label}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
              {/* Only show dates for paid subscriptions */}
              {subscription.type !== "none" && subscription.startDate && (
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider">Start Date</p>
                  <p className="text-white text-sm">{formatDate(subscription.startDate)}</p>
                </div>
              )}
              {subscription.type !== "none" && subscription.endDate && (
                <div>
                  <p className="text-white/40 text-xs uppercase tracking-wider">
                    {subscription.isCancelled ? "Access Until" : "Expires"}
                  </p>
                  <p className="text-white text-sm">{formatDate(subscription.endDate)}</p>
                </div>
              )}
              {subscription.type !== "none" && subscription.daysRemaining > 0 && (
                <div className="col-span-2">
                  <p className="text-white/40 text-xs uppercase tracking-wider">Days Remaining</p>
                  <p className={`text-lg font-medium ${getPlanColor()}`}>
                    {subscription.daysRemaining} days
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Free Tier Info */}
          {subscription.type === "none" && (
            <div className="bg-[#a69373]/10 border border-[#a69373]/20 rounded-xl p-4 mb-4">
              <p className="text-[#a69373]/80 text-sm">
                You have permanent access to basic features including Check-in, Mood & Energy tracking, 
                Journal, Gratitude, and limited Soundscapes & Meditations.
              </p>
            </div>
          )}

          {/* Cancelled Notice */}
          {subscription.isCancelled && subscription.isActive && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-medium text-sm">Subscription Cancelled</p>
                  <p className="text-amber-400/70 text-xs mt-1">
                    Your access will continue until {formatDate(subscription.endDate)}. 
                    After that, your account will revert to the free tier.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {/* Upgrade Button for Free users - prominent CTA */}
            {subscription.type === "none" && (
              <a
                href={MEMBERSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-semibold text-sm transition-colors"
                data-testid="upgrade-plan-btn"
              >
                <Crown className="w-4 h-4" />
                Unlock the full experience
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            
            {/* Upgrade Button - Show for Monthly/Quarterly users */}
            {(subscription.type === "monthly" || subscription.type === "quarterly") && (
              <a
                href={MEMBERSHIP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821] font-medium text-sm transition-colors"
                data-testid="upgrade-plan-btn"
              >
                <Crown className="w-4 h-4" />
                Upgrade Plan
                <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {/* Reactivate Button - Show if cancelled but still active */}
            {subscription.isCancelled && subscription.isActive && (
              <Button
                onClick={handleReactivate}
                disabled={reactivating}
                className="bg-[#7b9e7b] hover:bg-[#6a8d6a] text-white"
                data-testid="reactivate-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${reactivating ? "animate-spin" : ""}`} />
                {reactivating ? "Reactivating..." : "Reactivate"}
              </Button>
            )}

            {/* Cancel Button - Show if active and not already cancelled */}
            {subscription.isActive && !subscription.isCancelled && subscription.type !== "none" && (
              <Button
                variant="outline"
                onClick={() => setShowCancelConfirm(true)}
                className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                data-testid="cancel-subscription-btn"
              >
                Cancel Subscription
              </Button>
            )}
          </div>

          {/* Cancel Confirmation - Inline conditional rendering */}
          {showCancelConfirm && (
            <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium">Are you sure you want to cancel?</p>
                  <p className="text-red-400/70 text-sm mt-1">
                    Your access will continue until {formatDate(subscription.endDate)}. 
                    After that, your account will revert to the free tier.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="ghost"
                  onClick={() => setShowCancelConfirm(false)}
                  className="text-white/60 hover:text-white"
                >
                  Keep Subscription
                </Button>
                <Button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-cancel-btn"
                >
                  {cancelling ? "Cancelling..." : "Yes, Cancel"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History Card */}
      <Card className="glass-card overflow-hidden" data-testid="payment-history-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <History className="w-5 h-5 text-white/60" />
            </div>
            <div>
              <h3 className="text-white font-medium">Payment History</h3>
              <p className="text-white/50 text-sm">Your past transactions</p>
            </div>
          </div>

          {loadingHistory ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-12 bg-white/5 rounded-lg" />
              ))}
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="bg-white/5 rounded-xl p-6 text-center">
              <CreditCard className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <p className="text-white/50 text-sm">No payment history yet</p>
              {subscription.type === "none" && (
                <a
                  href={MEMBERSHIP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-[#a69373]/20 text-[#a69373] text-sm hover:bg-[#a69373]/30 transition-colors"
                >
                  Start your journey
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {paymentHistory.map((tx, index) => (
                <div 
                  key={tx.payment_id || index}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      tx.status === "COMPLETE" ? "bg-green-500/20" : "bg-white/10"
                    }`}>
                      {tx.status === "COMPLETE" ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <CreditCard className="w-4 h-4 text-white/40" />
                      )}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{tx.plan_type}</p>
                      <p className="text-white/40 text-xs">{formatDate(tx.date)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">{tx.amount}</p>
                    <p className={`text-xs ${tx.status === "COMPLETE" ? "text-green-400" : "text-white/40"}`}>
                      {tx.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Contact Support Link */}
          <p className="text-white/40 text-sm mt-4 pt-4 border-t border-white/10">
            Need to update your payment details?{" "}
            <a
              href="https://wa.me/27640606345?text=Hi%2C%20I%20need%20to%20update%20my%20payment%20details%20for%20my%20Compass%20subscription."
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#a69373] hover:underline"
              data-testid="contact-support-link"
            >
              Contact Support
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Memoize to prevent unnecessary re-renders from parent
export const SubscriptionManagement = memo(SubscriptionManagementComponent);
export default SubscriptionManagement;
